import React, { useState } from "react";
import { X, ShoppingBag, Minus, Plus, Trash2, Loader2, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/lib/CartContext";
import { buildOrderMessage, buildWhatsAppUrl } from "@/lib/brand";
import { trackClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", notes: "" };

const CartDrawer = () => {
  const { items, count, subtotal, open, setOpen, updateQty, removeItem, clear } = useCart();
  const [stage, setStage] = useState("cart"); // cart | checkout | done
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [orderNo, setOrderNo] = useState(null);

  if (!open) return null;

  const close = () => { setOpen(false); setTimeout(() => { setStage("cart"); setOrderNo(null); }, 250); };

  const placeCOD = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        items: items.map((it) => ({ slug: it.slug, name: it.name, size: it.size, qty: it.qty, price: it.price })),
      });
      trackClick(`cart-order-placed`);
      setOrderNo(data.order_no);
      setStage("done");
      clear();
    } catch {
      toast.error("Could not place order — please try again or WhatsApp us.");
    } finally { setBusy(false); }
  };

  const orderOnWhatsApp = () => {
    if (items.length === 0) return;
    const msg = buildOrderMessage(items, form);
    trackClick("cart-order-whatsapp");
    window.open(buildWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <aside className="relative w-full sm:max-w-md bg-[#FBF6EC] h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#3B2A1A]/10">
          <div>
            <p className="overline">{stage === "checkout" ? "Checkout" : stage === "done" ? "Confirmed" : "Your Basket"}</p>
            <p className="font-serif-display text-2xl text-[#3B2A1A] leading-tight mt-1">
              {stage === "done" ? "Thank you." : `${count} item${count === 1 ? "" : "s"}`}
            </p>
          </div>
          <button onClick={close} data-testid="cart-close-btn" className="text-[#8A7969] hover:text-[#3B2A1A]"><X size={20} /></button>
        </div>

        {/* Body */}
        {stage === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="cart-items-list">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-[#C89D3C]/10 flex items-center justify-center">
                    <ShoppingBag size={22} className="text-[#C89D3C]" />
                  </div>
                  <p className="font-serif-display text-2xl text-[#3B2A1A] mt-6">Your basket is empty.</p>
                  <p className="text-sm text-[#8A7969] font-light mt-2 max-w-xs">Add ritual essentials or a curated puja kit — order via COD or WhatsApp in one tap.</p>
                </div>
              ) : items.map((it) => (
                <div key={`${it.slug}::${it.size || ""}`} className="flex gap-4 py-4 border-b border-[#3B2A1A]/10" data-testid={`cart-item-${it.slug}`}>
                  <div className="w-20 h-20 bg-[#F5EFE3] flex-shrink-0 overflow-hidden">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-display text-lg text-[#3B2A1A] leading-tight truncate">{it.name}</p>
                    {it.size && <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A7969] mt-1">Size · {it.size}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border border-[#3B2A1A]/20">
                        <button onClick={() => updateQty(it.slug, it.size, it.qty - 1)} data-testid={`cart-qty-minus-${it.slug}`} className="p-2 text-[#3B2A1A] hover:bg-[#F5EFE3]"><Minus size={12} /></button>
                        <span className="px-3 text-sm font-medium text-[#3B2A1A]" data-testid={`cart-qty-${it.slug}`}>{it.qty}</span>
                        <button onClick={() => updateQty(it.slug, it.size, it.qty + 1)} data-testid={`cart-qty-plus-${it.slug}`} className="p-2 text-[#3B2A1A] hover:bg-[#F5EFE3]"><Plus size={12} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        {it.price && <span className="text-sm font-serif-display text-[#3B2A1A]">{it.price}</span>}
                        <button onClick={() => removeItem(it.slug, it.size)} data-testid={`cart-remove-${it.slug}`} className="text-[#8A7969] hover:text-[#8A1A2B]"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#3B2A1A]/10 px-6 py-5 bg-[#F5EFE3]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs uppercase tracking-widest text-[#8A7969]">Estimated Subtotal</span>
                  <span className="font-serif-display text-2xl text-[#3B2A1A]" data-testid="cart-subtotal">₹ {subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={orderOnWhatsApp} data-testid="cart-whatsapp-btn"
                    className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white text-xs uppercase tracking-[0.18em] font-semibold py-3.5 transition-colors">
                    <MessageCircle size={14} /> WhatsApp
                  </button>
                  <button onClick={() => setStage("checkout")} data-testid="cart-checkout-btn" className="btn-primary justify-center py-3.5">
                    Checkout (COD)
                  </button>
                </div>
                <p className="text-[10px] text-center text-[#8A7969] font-light mt-3">Order on WhatsApp for instant confirmation • Cash on Delivery available across India</p>
              </div>
            )}
          </>
        )}

        {stage === "checkout" && (
          <form onSubmit={placeCOD} className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-testid="cart-checkout-form">
            <button type="button" onClick={() => setStage("cart")} className="text-xs uppercase tracking-[0.18em] text-[#8A7969] hover:text-[#3B2A1A]">← Back to basket</button>

            <div className="bg-white border border-[#3B2A1A]/10 p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-widest text-[#8A7969]">Order Total</span>
                <span className="font-serif-display text-xl text-[#3B2A1A]">₹ {subtotal.toLocaleString("en-IN")}</span>
              </div>
              <p className="text-[11px] text-[#8A7969] font-light mt-1">{count} item{count === 1 ? "" : "s"} · Cash on Delivery</p>
            </div>

            {[["name", "Full Name"], ["phone", "Phone"], ["email", "Email (optional)"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">{l}</label>
                <input required={k !== "email"} type={k === "email" ? "email" : "text"} value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`cart-input-${k}`}
                  className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Delivery Address</label>
              <textarea required rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="cart-input-address"
                className="w-full bg-transparent border border-[#3B2A1A]/20 p-3 mt-2 text-sm focus:outline-none focus:border-[#C89D3C]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">{l}</label>
                  <input required={k !== "state"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`cart-input-${k}`}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 focus:outline-none focus:border-[#C89D3C]" />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 sticky bottom-0 bg-[#FBF6EC] pb-2">
              <button type="button" onClick={orderOnWhatsApp} data-testid="cart-checkout-whatsapp-btn"
                className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white text-xs uppercase tracking-[0.18em] font-semibold py-3.5 transition-colors">
                <MessageCircle size={14} /> WhatsApp
              </button>
              <button type="submit" disabled={busy} data-testid="cart-place-order-btn" className="btn-primary justify-center py-3.5">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <>Place Order</>}
              </button>
            </div>
          </form>
        )}

        {stage === "done" && (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="cart-order-success">
            <div className="w-16 h-16 rounded-full bg-[#C89D3C]/10 flex items-center justify-center">
              <ShoppingBag size={22} className="text-[#C89D3C]" />
            </div>
            <p className="font-serif-display text-2xl text-[#3B2A1A] mt-6">Order received.</p>
            <p className="text-sm text-[#5C4A3A] mt-3 font-light">Your order number is</p>
            <p className="font-serif-display text-3xl text-[#C89D3C] mt-2 tracking-wider">{orderNo}</p>
            <p className="text-sm text-[#5C4A3A] mt-6 font-light max-w-xs">Our team will call you within 24 hours to confirm and arrange delivery.</p>
            <button onClick={close} className="btn-primary mt-8" data-testid="cart-order-success-close">Continue Shopping</button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
